// src/pages/LoginPage.jsx
// Porte d'entrée « Le Cabinet » — palier 9 de l'intégration React.
// Réf. visuelle EXACTE : design-mockups/concepts-v2/le-cabinet/login.html
//
// Écran PUBLIC (pas de topnav, pas de viewbar). Parti-pris : avant même de se
// connecter, on RESSENT le cabinet vivant.
//   • Volet GAUCHE  — mise en scène : un mot de Claire (orchestratrice) +
//     l'équipe au travail en direct, sur le bandeau sombre signature.
//   • Volet DROIT   — connexion e-mail + mot de passe, ivoire, bordeaux au
//     scalpel. Gère aussi inscription (essai gratuit) et mot de passe oublié.
//   • Mentions de confiance (RGPD · données en UE · secret pro) en pied.
//   2 colonnes desktop · 1 colonne mobile (logo en haut, formulaire, mentions).
//
// SOCLE : on consomme les tokens `cab` (var(--cab-*)) + fonts Fraunces / Geist
// Mono (fonts.serifDisplay / fonts.monoCab) + les keyframes globales `cab*`
// (cabRing, cabBlink, cabRipple, cabRot) définies dans index.css. La page peint
// sa propre surface — elle ne touche PAS aux variables « E » globales.
//
// ⚠️ NON-CASSANT : TOUTE la logique d'auth est préservée — `signIn`, `signUp`
// (+ POST /api/auth/signup, redirection Stripe checkout), `resetPassword`,
// validation du mot de passe, gestion d'erreurs, redirections, états loading.
// On réhabille le RENDU, pas les flux. (Pas d'OAuth : AuthContext n'expose pas
// de provider — on n'invente pas de bouton branché sur un handler inexistant.)

import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { translateAuthError } from '../utils/auth-errors'
import { useIsMobile } from '../hooks/useIsMobile'
import IAvocatLogo from '../components/IAvocatLogo'
import { AGENTS } from '../data/agents'
import { PLANS_CATALOG } from '../lib/plansCatalog'
import { cab as K, fonts as F } from '../theme'

// ─── Photos d'équipe (données réelles AGENTS, fallback Supabase) ───
const photo = (id, fallback) =>
  AGENTS.find(a => a.id === id)?.photoUrl
  || `https://mhixfakpafenionfrpto.supabase.co/storage/v1/object/public/agent-photos/${fallback || id}.jpg`

const CLAIRE = photo('claire')

// ─── Icônes (stroke currentColor) ───
const Ic = {
  shield: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.6" /><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  shieldLine: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.5" /></svg>),
  mail: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="1.7" /><path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  lock: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="4" y="11" width="16" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.7" /><path d="M8 11V8a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><circle cx="12" cy="16" r="1.4" fill="currentColor" /></svg>),
  user: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.7" /><path d="M4 20c0-3.3 3.6-6 8-6s8 2.7 8 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  scale: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 3v18M7 21h10M6 7h12M6 7l-3 6a3 3 0 006 0L6 7zm12 0l-3 6a3 3 0 006 0l-3-6z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  eye: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="1.7" /><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" /></svg>),
  eyeOff: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><path d="M10.6 6.2A9.7 9.7 0 0112 6c6.5 0 10 7 10 7a17 17 0 01-3 3.7M6.4 6.5A17 17 0 002 13s3.5 7 10 7a9.6 9.6 0 004.3-1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  check: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  arrow: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  back: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M19 12H5M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  spark: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
}

// Visage d'un collaborateur au travail (volet gauche)
function WorkerFace({ src, alt, seam }) {
  return (
    <span style={{ position: 'relative', width: 40, height: 40, flex: 'none' }}>
      <img
        src={src} alt={alt} loading="lazy"
        onError={(e) => { e.currentTarget.style.opacity = '0' }}
        style={{ width: '100%', height: '100%', borderRadius: 11, objectFit: 'cover', objectPosition: 'center top', border: '1px solid rgba(255,255,255,.18)' }}
      />
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: -3, height: 3, borderRadius: 99, background: seam }} />
    </span>
  )
}

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState('login') // 'login' | 'signup' | 'forgot' | 'recovery'
  const [resetSent, setResetSent] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [barreau, setBarreau] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [selectedPlan, setSelectedPlan] = useState('cabinet') // formule mise en avant (PLANS_CATALOG.highlight)
  const [remember, setRemember] = useState(true)
  const { signIn, signUp, resetPassword, updatePassword } = useAuth()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  // Lien de réinitialisation : supabase-js consomme le hash (#access_token…
  // type=recovery) à l'arrivée sur /login et émet PASSWORD_RECOVERY → on
  // bascule sur le formulaire « nouveau mot de passe ». Sans cet écran, le
  // lien envoyé par e-mail connectait l'utilisateur sans jamais lui permettre
  // de changer son mot de passe.
  useEffect(() => {
    if (!supabase) return
    if (window.location.hash.includes('type=recovery')) setMode('recovery')
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') { setMode('recovery'); setError('') }
    })
    return () => subscription.unsubscribe()
  }, [])

  const passwordChecks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  }
  const isPasswordValid = Object.values(passwordChecks).every(Boolean)
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0

  // ════════════════════════════════════════════════════════════════════════
  // LOGIQUE D'AUTH — PRÉSERVÉE À L'IDENTIQUE (ne pas modifier les flux)
  // ════════════════════════════════════════════════════════════════════════
  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'forgot') {
        await resetPassword(email)
        setResetSent(true)
      } else if (mode === 'recovery') {
        await updatePassword(password)
        navigate('/')
      } else if (mode === 'signup') {
        const { user: newUser, session: newSession } = await signUp(email, password)
        if (!newUser) throw new Error('Erreur lors de la création du compte')

        const signupRes = await fetch('/api/auth/signup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(newSession?.access_token ? { 'Authorization': `Bearer ${newSession.access_token}` } : {}),
          },
          body: JSON.stringify({
            auth_user_id: newUser.id,
            email,
            first_name: firstName,
            last_name: lastName,
            barreau: barreau || null,
            plan: selectedPlan,
          }),
        })
        const signupData = await signupRes.json()
        if (!signupRes.ok) throw new Error(signupData.error || 'Erreur lors de la création du profil')
        const lawyerId = signupData.lawyer_id

        // Checkout requires a Supabase JWT (lawyer_id/email derived server-side).
        // The session exists right after signUp when email confirmation is off;
        // otherwise we skip the redirect and let the user start checkout later
        // from the dashboard (Paramètres → Abonnement).
        const accessToken = newSession?.access_token
        if (lawyerId && accessToken) {
          try {
            const stripeRes = await fetch('/api/stripe/create-checkout', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
              body: JSON.stringify({ plan_key: selectedPlan }),
            })
            const stripeData = await stripeRes.json()
            if (stripeData.checkout_url) { window.location.href = stripeData.checkout_url; return }
          } catch (stripeErr) { console.error('Stripe redirect error:', stripeErr) }
        }

        navigate('/')
      } else {
        await signIn(email, password)
        navigate('/')
      }
    } catch (err) {
      setError(
        mode === 'forgot'
          ? "Impossible d'envoyer le lien de réinitialisation."
          : mode === 'recovery'
            ? 'Impossible de mettre à jour le mot de passe. Le lien a peut-être expiré — refaites « Mot de passe oublié ».'
            : translateAuthError(err)
      )
    } finally {
      setLoading(false)
    }
  }

  // ── Copie selon le mode ──
  const eyebrow = mode === 'recovery' ? 'Récupération' : mode === 'forgot' ? 'Récupération' : mode === 'signup' ? 'Essai gratuit' : 'Connexion'
  const heading = mode === 'recovery' ? 'Nouveau mot de passe' : mode === 'forgot' ? 'Mot de passe oublié' : mode === 'signup' ? 'Ouvrez votre cabinet.' : 'Bon retour, Maître.'
  const subheading = mode === 'recovery'
    ? 'Choisissez un nouveau mot de passe pour votre compte.'
    : mode === 'forgot'
      ? 'Entrez votre e-mail : un lien de réinitialisation vous sera envoyé.'
      : mode === 'signup'
        ? 'Essai gratuit 14 jours — sans engagement, sans carte bancaire.'
        : "Accédez à votre cabinet et reprenez là où l'équipe s'est arrêtée."
  const submitLabel = mode === 'recovery' ? 'Définir le mot de passe' : mode === 'forgot' ? 'Envoyer le lien' : mode === 'signup' ? 'Créer mon compte' : 'Entrer dans le cabinet'

  const submitDisabled = loading || ((mode === 'signup' || mode === 'recovery') && (!isPasswordValid || !passwordsMatch))

  // ─── Primitives de champ (volet droit) ───
  const fieldInput = (hasLead = true, hasTrailing = false) => ({
    width: '100%',
    height: 52,
    borderRadius: 14,
    background: K.card,
    border: `1px solid ${K.hair2}`,
    padding: `0 ${hasTrailing ? 50 : 16}px 0 ${hasLead ? 44 : 16}px`,
    fontFamily: F.sans,
    fontSize: 15,
    color: K.ink,
    letterSpacing: '-0.01em',
    boxShadow: K.lift1,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color .2s, box-shadow .2s',
  })
  const onFieldFocus = e => {
    e.target.style.borderColor = K.bordeauxHi
    e.target.style.boxShadow = `0 0 0 3px ${K.bordeauxTint}, ${K.lift1}`
  }
  const onFieldBlur = e => {
    e.target.style.borderColor = K.hair2
    e.target.style.boxShadow = K.lift1
  }

  const Lead = ({ children }) => (
    <span aria-hidden="true" style={{ position: 'absolute', left: 15, top: '50%', transform: 'translateY(-50%)', width: 17, height: 17, color: K.ink4, pointerEvents: 'none' }}>
      {children}
    </span>
  )
  const FieldLabel = ({ htmlFor, children, aside }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
      <label htmlFor={htmlFor} style={{ fontSize: 12.5, fontWeight: 600, color: K.ink2, letterSpacing: '.01em' }}>{children}</label>
      {aside}
    </div>
  )

  return (
    <div style={{ position: 'relative', minHeight: '100vh', fontFamily: F.sans, background: K.paper, color: K.ink, overflowX: 'hidden' }}>

      {/* ── Ambiance de fond (aura + grain), identique au socle ── */}
      <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <span style={{ position: 'absolute', width: 680, height: 680, left: -160, top: -240, opacity: .5, borderRadius: '50%', filter: 'blur(110px)', background: 'radial-gradient(circle, rgba(168,50,74,.20), transparent 66%)' }} />
        <span style={{ position: 'absolute', width: 620, height: 620, right: -180, bottom: -220, opacity: .45, borderRadius: '50%', filter: 'blur(110px)', background: 'radial-gradient(circle, rgba(46,114,184,.14), transparent 70%)' }} />
      </div>

      <main style={{
        position: 'relative', zIndex: 2, minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.06fr .94fr',
        alignItems: 'stretch',
      }}>

        {/* ════════════════════════════════════════════════════════════════
            VOLET GAUCHE — LE CABINET VIVANT (mise en scène, bandeau sombre)
            Sur mobile : se replie en bannière compacte SOUS le formulaire.
            ════════════════════════════════════════════════════════════════ */}
        <section
          aria-label="Votre cabinet de 20 collaborateurs"
          style={{
            position: 'relative', overflow: 'hidden', order: isMobile ? 2 : 0,
            display: 'flex', flexDirection: 'column',
            padding: isMobile ? '26px 20px 24px' : '40px 46px 38px',
            margin: isMobile ? '0 16px 18px' : 0,
            borderRadius: isMobile ? K.rXl : 0,
            color: '#F6ECEE',
            background: 'radial-gradient(125% 120% at 4% 0%, rgba(168,50,74,.42), transparent 56%), radial-gradient(90% 120% at 100% 100%, rgba(46,114,184,.14), transparent 60%), linear-gradient(160deg, #3a1620, #271017)',
          }}
        >
          {/* logo (variante claire) — caché en mobile (le logo mobile vit dans le volet droit) */}
          {!isMobile && (
            <a href="https://iavocat.ai" aria-label="IAvocat.ai, accueil" style={{ textDecoration: 'none', alignSelf: 'flex-start' }}>
              <IAvocatLogo size={32} tone="light" />
            </a>
          )}

          {/* kicker */}
          <div style={{
            fontFamily: F.monoCab, fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase',
            color: 'rgba(255,190,202,.92)', marginTop: isMobile ? 0 : 30,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span aria-hidden="true" style={{ width: 26, height: 1, background: K.gildHi }} />
            Votre cabinet · 20 collaborateurs IA
          </div>

          {/* ── Un mot de Claire ── */}
          <div style={{ marginTop: 24, display: 'flex', gap: isMobile ? 15 : 20, alignItems: 'flex-start' }}>
            <span style={{ position: 'relative', width: isMobile ? 56 : 66, height: isMobile ? 56 : 66, flex: 'none' }}>
              <span aria-hidden="true" style={{ position: 'absolute', inset: -6, borderRadius: 23, border: '1.5px solid rgba(255,180,195,.7)', opacity: .6, animation: 'cabRing 3.4s var(--cab-ease-soft) infinite' }} />
              <img
                src={CLAIRE} alt="Claire, orchestratrice du cabinet" loading="lazy"
                style={{ width: '100%', height: '100%', borderRadius: 18, objectFit: 'cover', objectPosition: 'center top', border: '1.5px solid rgba(255,255,255,.28)', boxShadow: '0 10px 26px rgba(0,0,0,.5)' }}
              />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: F.monoCab, fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase', color: 'rgba(255,190,202,.95)', marginBottom: 9, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#5BE0A6', boxShadow: '0 0 0 3px rgba(91,224,166,.22)', animation: 'cabBlink 2.6s infinite' }} />
                Claire · Orchestratrice
              </div>
              <h1 style={{ fontFamily: F.serifDisplay, fontWeight: 400, fontSize: 'clamp(26px,2.5vw,36px)', lineHeight: 1.16, letterSpacing: '-.02em', color: '#fff', maxWidth: isMobile ? 'none' : '18ch' }}>
                Le cabinet vous attend, <b style={{ fontWeight: 600, color: '#fff' }}>Maître</b>.
              </h1>
              <p style={{ marginTop: 14, fontSize: 14, color: 'rgba(246,236,238,.72)', lineHeight: 1.5, maxWidth: '40ch' }}>
                Cette nuit, l'équipe a fait avancer <b style={{ color: '#fff', fontWeight: 600 }}>3 dossiers</b> et préparé <b style={{ color: '#fff', fontWeight: 600 }}>7 réponses</b>. Connectez-vous : tout est prêt à être tranché.
              </p>
            </div>
          </div>

          {/* ── L'équipe au travail, en direct ── */}
          <div style={{ marginTop: 30 }}>
            <div style={{ fontFamily: F.monoCab, fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(246,236,238,.55)', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>En ce moment dans votre cabinet</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', color: '#5BE0A6', background: 'rgba(91,224,166,.12)', padding: '3px 9px 3px 8px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: '#5BE0A6', animation: 'cabRipple 2s var(--cab-ease-soft) infinite' }} />
                EN DIRECT
              </span>
            </div>

            {/* Thomas — en cours */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '11px 0' }}>
              <WorkerFace src={photo('thomas')} alt="Thomas" seam="var(--cab-affaires)" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, color: 'rgba(246,236,238,.86)', lineHeight: 1.35 }}>
                  <b style={{ color: '#fff', fontWeight: 600 }}>Thomas</b> finalise la mise en demeure <span style={{ color: 'rgba(246,236,238,.5)' }}>— Martin c/ Durand</span>
                </div>
                <div style={{ fontSize: 11, color: 'rgba(246,236,238,.5)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: '#8fb2e0', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', display: 'inline-block', animation: 'cabRot .9s linear infinite' }} />
                    Rédaction
                  </span>
                  <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(246,236,238,.4)' }} />
                  relit ses citations
                </div>
                <div style={{ marginTop: 7, height: 4, borderRadius: 99, background: 'rgba(255,255,255,.12)', overflow: 'hidden', maxWidth: 200 }}>
                  <i style={{ display: 'block', height: '100%', width: '72%', borderRadius: 99, background: 'linear-gradient(90deg, var(--cab-bordeaux-hi), var(--cab-gild-2))' }} />
                </div>
              </div>
              <span style={{ flex: 'none', alignSelf: 'flex-start', fontFamily: F.monoCab, fontSize: 10, color: 'rgba(246,236,238,.45)', letterSpacing: '.04em' }}>72 %</span>
            </div>

            {/* Sophie — prêt à valider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '11px 0', borderTop: '1px solid rgba(255,255,255,.09)' }}>
              <WorkerFace src={photo('sophie')} alt="Sophie" seam="var(--cab-secretariat)" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, color: 'rgba(246,236,238,.86)', lineHeight: 1.35 }}>
                  <b style={{ color: '#fff', fontWeight: 600 }}>Sophie</b> a préparé <b style={{ color: '#fff', fontWeight: 600 }}>5 réponses</b> à vos mails
                </div>
                <div style={{ fontSize: 11, color: 'rgba(246,236,238,.5)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#5BE0A6', background: 'rgba(91,224,166,.12)', padding: '2px 8px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Ic.check style={{ width: 11, height: 11 }} />Prêt à valider
                  </span>
                  <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(246,236,238,.4)' }} />
                  il y a 6 min
                </div>
              </div>
              <span style={{ flex: 'none', alignSelf: 'flex-start', fontFamily: F.monoCab, fontSize: 10, color: 'rgba(246,236,238,.45)', letterSpacing: '.04em' }}>5/7</span>
            </div>

            {/* Margaux — en attente de vous (masquée sur mobile pour alléger) */}
            {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '11px 0', borderTop: '1px solid rgba(255,255,255,.09)' }}>
                <WorkerFace src={photo('margaux')} alt="Margaux" seam="var(--cab-famille)" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, color: 'rgba(246,236,238,.86)', lineHeight: 1.35 }}>
                    <b style={{ color: '#fff', fontWeight: 600 }}>Margaux</b> a terminé la requête JAF <span style={{ color: 'rgba(246,236,238,.5)' }}>— Renard</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(246,236,238,.5)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#5BE0A6', background: 'rgba(91,224,166,.12)', padding: '2px 8px', borderRadius: 99, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <Ic.check style={{ width: 11, height: 11 }} />En attente de vous
                    </span>
                    <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(246,236,238,.4)' }} />
                    100 %
                  </div>
                </div>
                <span style={{ flex: 'none', alignSelf: 'flex-start', fontFamily: F.monoCab, fontSize: 10, color: 'rgba(246,236,238,.45)', letterSpacing: '.04em' }}>100 %</span>
              </div>
            )}
          </div>

          {/* ── Le banc : reste de l'équipe disponible ── */}
          <div style={{ marginTop: isMobile ? 18 : 22, display: 'flex', alignItems: 'center', gap: 15 }}>
            <span aria-hidden="true" style={{ display: 'flex' }}>
              {[
                { id: 'louise' }, { id: 'pierre' }, { id: 'antoine' }, { id: 'francoise' },
              ].map((a, i) => (
                <img
                  key={a.id} src={photo(a.id)} alt=""
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                  style={{ width: 34, height: 34, borderRadius: 10, objectFit: 'cover', objectPosition: 'center top', border: '2px solid #2c1218', marginLeft: i === 0 ? 0 : -9, boxShadow: '0 4px 12px rgba(0,0,0,.4)' }}
                />
              ))}
              <span style={{ width: 34, height: 34, borderRadius: 10, marginLeft: -9, background: 'rgba(255,255,255,.1)', border: '2px solid #2c1218', display: 'grid', placeItems: 'center', fontFamily: F.monoCab, fontSize: 11, fontWeight: 600, color: 'rgba(246,236,238,.85)' }}>+12</span>
            </span>
            <span style={{ fontSize: 12.5, color: 'rgba(246,236,238,.62)', lineHeight: 1.4 }}>
              <b style={{ color: '#fff', fontWeight: 600 }}>16 spécialistes</b> disponibles, prêts à prendre un dossier dès votre arrivée.
            </span>
          </div>

          {/* espace souple */}
          <div style={{ flex: 1, minHeight: 18 }} />

          {/* ── Socle souveraineté (pied du volet sombre) ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, paddingTop: 22, borderTop: '1px solid rgba(255,255,255,.1)', fontSize: 12, color: 'rgba(246,236,238,.62)', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <Ic.shield style={{ width: 15, height: 15, color: '#5BE0A6', flex: 'none' }} />
              Données hébergées en Union européenne
            </span>
            <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(246,236,238,.35)' }} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>Conforme RGPD</span>
            <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(246,236,238,.35)' }} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>Couvert par le secret professionnel</span>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════
            VOLET DROIT — CONNEXION / INSCRIPTION / RÉCUPÉRATION
            ════════════════════════════════════════════════════════════════ */}
        <section
          aria-label="Connexion à votre cabinet"
          style={{
            position: 'relative', order: isMobile ? 1 : 0,
            display: 'flex', flexDirection: 'column',
            justifyContent: isMobile ? 'flex-start' : 'center',
            padding: isMobile ? '26px 20px 8px' : '40px 52px',
            minHeight: isMobile ? 'auto' : '100vh',
            background: K.card,
          }}
        >
          {/* shield top-right (desktop) — sur mobile il repart dans le pied légal */}
          {!isMobile && (
            <div style={{ position: 'absolute', top: 34, left: 52, right: 52, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16 }}>
              <span title="Hébergement souverain" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 99, background: K.card, border: `1px solid ${K.hair}`, fontSize: 12, color: K.ink2, fontWeight: 500, boxShadow: K.lift1 }}>
                <Ic.shield style={{ width: 14, height: 14, color: K.verified }} />
                Données en UE · RGPD
              </span>
            </div>
          )}

          {/* logo (mobile seulement), ancré en haut */}
          {isMobile && (
            <a href="https://iavocat.ai" aria-label="IAvocat.ai, accueil" style={{ display: 'flex', justifyContent: 'center', margin: '8px 0 26px', textDecoration: 'none' }}>
              <IAvocatLogo size={28} />
            </a>
          )}

          {/* carte d'authentification (centrée). Sur mobile : colonne flex pour
              pousser logo en haut / mentions en bas. */}
          <div style={{
            width: '100%', maxWidth: isMobile ? 440 : 404, margin: '0 auto',
            ...(isMobile && { display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 96px)' }),
          }}>

            {/* zone centrale (mobile) : centre le formulaire entre logo et mentions */}
            <div style={isMobile ? { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0 } : undefined}>

              {/* en-tête */}
              <div style={{ marginBottom: 26 }}>
                <div style={{ fontFamily: F.monoCab, fontSize: 11, letterSpacing: '.2em', textTransform: 'uppercase', color: K.ink3, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 11 }}>
                  <span aria-hidden="true" style={{ width: 24, height: 1, background: K.bordeaux }} />
                  {eyebrow}
                </div>
                <h2 style={{ fontFamily: F.serifDisplay, fontWeight: 500, fontSize: 'clamp(28px,3vw,36px)', lineHeight: 1.08, letterSpacing: '-.02em', color: K.ink, margin: 0 }}>
                  {heading}
                </h2>
                <p style={{ fontSize: 14, color: K.ink2, marginTop: 10, lineHeight: 1.5, maxWidth: '36ch' }}>
                  {subheading}
                </p>
              </div>

              {resetSent ? (
                /* ── Confirmation : lien de réinitialisation envoyé ── */
                <div style={{ padding: '30px 24px', borderRadius: K.rMd, border: `1px solid ${K.hair2}`, background: K.cardSoft, textAlign: 'center', boxShadow: K.lift1 }}>
                  <span style={{ display: 'inline-grid', placeItems: 'center', width: 46, height: 46, borderRadius: '50%', background: 'rgba(21,135,95,.12)', color: K.verified, marginBottom: 14 }}>
                    <Ic.check style={{ width: 22, height: 22 }} />
                  </span>
                  <p style={{ fontFamily: F.serifDisplay, fontWeight: 500, color: K.ink, fontSize: 20, margin: '0 0 6px' }}>Lien envoyé.</p>
                  <p style={{ fontSize: 13.5, color: K.ink2, margin: '0 0 20px', lineHeight: 1.5 }}>
                    Vérifiez votre boîte mail et suivez les instructions pour définir un nouveau mot de passe.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setResetSent(false) }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', color: K.bordeaux, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', fontFamily: F.sans }}
                  >
                    <Ic.back style={{ width: 15, height: 15 }} />Retour à la connexion
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>

                  {/* erreur d'auth (préservée) */}
                  {error && (
                    <div role="alert" style={{ marginBottom: 18, padding: '11px 14px', borderRadius: K.rSm, fontSize: 13, fontWeight: 500, background: 'rgba(122,31,46,.06)', color: K.bordeaux, border: `1px solid ${K.bordeauxTint2}` }}>
                      {error}
                    </div>
                  )}

                  {/* e-mail (masqué en recovery : la session vient du lien) */}
                  {mode !== 'recovery' && (
                  <div style={{ marginBottom: 15 }}>
                    <FieldLabel htmlFor="email">Adresse e-mail professionnelle</FieldLabel>
                    <div style={{ position: 'relative' }}>
                      <Lead><Ic.mail style={{ width: 17, height: 17 }} /></Lead>
                      <input
                        id="email" name="email" type="email" autoComplete="email" inputMode="email"
                        value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="vous@cabinet.fr" required aria-required="true"
                        style={fieldInput(true)} onFocus={onFieldFocus} onBlur={onFieldBlur}
                      />
                    </div>
                  </div>
                  )}

                  {/* champs inscription (préservés) */}
                  {mode === 'signup' && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 15 }}>
                        <div>
                          <FieldLabel htmlFor="firstName">Prénom</FieldLabel>
                          <div style={{ position: 'relative' }}>
                            <Lead><Ic.user style={{ width: 17, height: 17 }} /></Lead>
                            <input id="firstName" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Sophie" required style={fieldInput(true)} onFocus={onFieldFocus} onBlur={onFieldBlur} />
                          </div>
                        </div>
                        <div>
                          <FieldLabel htmlFor="lastName">Nom</FieldLabel>
                          <input id="lastName" type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="DURAND" required style={fieldInput(false)} onFocus={onFieldFocus} onBlur={onFieldBlur} />
                        </div>
                      </div>
                      <div style={{ marginBottom: 15 }}>
                        <FieldLabel htmlFor="barreau">Barreau</FieldLabel>
                        <div style={{ position: 'relative' }}>
                          <Lead><Ic.scale style={{ width: 17, height: 17 }} /></Lead>
                          <input id="barreau" type="text" value={barreau} onChange={e => setBarreau(e.target.value)} placeholder="Paris, Lyon, Marseille…" style={fieldInput(true)} onFocus={onFieldFocus} onBlur={onFieldBlur} />
                        </div>
                      </div>
                      <div style={{ marginBottom: 15 }}>
                        <FieldLabel>Formule</FieldLabel>
                        {/* Catalogue canonique partagé (src/lib/plansCatalog.js) — mêmes
                            clés que api/_lib/plans.js (PUBLIC_PLANS). Les anciens libellés
                            codés en dur (secretariat/collaborateur/individuel/cabinet@999€)
                            n'existaient pas côté backend → create-checkout renvoyait 400. */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))', gap: 8 }}>
                          {PLANS_CATALOG.map(p => {
                            const active = selectedPlan === p.key
                            return (
                              <button
                                key={p.key} type="button" onClick={() => setSelectedPlan(p.key)}
                                aria-pressed={active}
                                style={{
                                  position: 'relative',
                                  padding: '11px 12px', borderRadius: K.rSm, textAlign: 'left', cursor: 'pointer', fontFamily: F.sans, transition: 'all .15s',
                                  border: active ? `1.5px solid ${K.bordeaux}` : `1px solid ${K.hair2}`,
                                  background: active ? K.bordeauxTint : K.card,
                                  boxShadow: active ? `0 0 0 3px ${K.bordeauxTint}` : K.lift1,
                                }}
                              >
                                <div style={{ fontSize: 13, fontWeight: 600, color: active ? K.bordeaux : K.ink, marginBottom: 2 }}>{p.label}</div>
                                <div style={{ fontFamily: F.monoCab, fontSize: 10, color: K.ink3, letterSpacing: '.04em' }}>{p.price}{p.period}</div>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  {/* mot de passe (login + signup + recovery) */}
                  {(mode === 'login' || mode === 'signup' || mode === 'recovery') && (
                    <div style={{ marginBottom: mode === 'signup' || mode === 'recovery' ? 14 : 4 }}>
                      <FieldLabel
                        htmlFor="password"
                        aside={mode === 'login' ? (
                          <button type="button" onClick={() => { setMode('forgot'); setError('') }} style={{ fontSize: 12, fontWeight: 600, color: K.bordeaux, background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.sans, padding: 0 }}>
                            Mot de passe oublié&nbsp;?
                          </button>
                        ) : null}
                      >
                        {mode === 'recovery' ? 'Nouveau mot de passe' : 'Mot de passe'}
                      </FieldLabel>
                      <div style={{ position: 'relative' }}>
                        <Lead><Ic.lock style={{ width: 17, height: 17 }} /></Lead>
                        <input
                          id="password" name="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete={mode === 'signup' || mode === 'recovery' ? 'new-password' : 'current-password'}
                          value={password} onChange={e => setPassword(e.target.value)}
                          placeholder="••••••••••" required aria-required="true"
                          style={fieldInput(true, true)} onFocus={onFieldFocus} onBlur={onFieldBlur}
                        />
                        <button
                          type="button" onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                          aria-pressed={showPassword}
                          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 38, height: 38, borderRadius: 10, display: 'grid', placeItems: 'center', color: K.ink3, background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          {showPassword ? <Ic.eyeOff style={{ width: 18, height: 18 }} /> : <Ic.eye style={{ width: 18, height: 18 }} />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* critères mot de passe + confirmation (signup + recovery) */}
                  {(mode === 'signup' || mode === 'recovery') && (
                    <>
                      <div style={{ marginBottom: 16, padding: '0 2px' }}>
                        {[
                          { key: 'minLength', label: 'Au moins 8 caractères' },
                          { key: 'hasUppercase', label: 'Au moins 1 lettre majuscule' },
                          { key: 'hasNumber', label: 'Au moins 1 chiffre' },
                        ].map(c => {
                          const ok = passwordChecks[c.key]
                          return (
                            <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 7, lineHeight: 1.7 }}>
                              <span style={{ fontSize: 11, color: ok ? K.verified : K.ink4, fontFamily: F.monoCab, width: 12, display: 'inline-flex', justifyContent: 'center' }}>{ok ? '✓' : '○'}</span>
                              <span style={{ fontSize: 12, fontFamily: F.sans, color: ok ? K.verified : K.ink2 }}>{c.label}</span>
                            </div>
                          )
                        })}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <FieldLabel htmlFor="confirmPassword">Confirmer le mot de passe</FieldLabel>
                        <div style={{ position: 'relative' }}>
                          <Lead><Ic.lock style={{ width: 17, height: 17 }} /></Lead>
                          <input
                            id="confirmPassword" type="password" autoComplete="new-password"
                            value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                            placeholder="••••••••••" required
                            style={fieldInput(true)} onFocus={onFieldFocus} onBlur={onFieldBlur}
                          />
                        </div>
                        {confirmPassword.length > 0 && !passwordsMatch && (
                          <p style={{ margin: '6px 0 0', fontSize: 12, fontFamily: F.sans, color: K.bordeaux }}>
                            Les mots de passe ne correspondent pas
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {/* rester connecté (login uniquement) */}
                  {mode === 'login' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 20px', fontSize: 13, color: K.ink2, cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                      />
                      <span aria-hidden="true" style={{
                        width: 19, height: 19, borderRadius: 6, flex: 'none', display: 'grid', placeItems: 'center', transition: '.18s', boxShadow: K.lift1,
                        border: remember ? `1.5px solid ${K.bordeaux}` : `1.5px solid ${K.hair3}`,
                        background: remember ? 'linear-gradient(180deg, var(--cab-bordeaux-hi), var(--cab-bordeaux))' : K.card,
                      }}>
                        <Ic.check style={{ width: 12, height: 12, color: '#fff', opacity: remember ? 1 : 0, transform: remember ? 'scale(1)' : 'scale(.6)', transition: '.18s' }} />
                      </span>
                      Rester connecté sur cet appareil
                    </label>
                  )}

                  {mode === 'forgot' && <div style={{ height: 8 }} />}

                  {/* bouton principal */}
                  <button
                    type="submit" disabled={submitDisabled}
                    style={{
                      width: '100%', height: 54, borderRadius: 14, fontWeight: 700, fontSize: 15, color: '#fff',
                      background: 'linear-gradient(180deg, var(--cab-bordeaux-hi), var(--cab-bordeaux))',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10, letterSpacing: '-.01em',
                      boxShadow: '0 12px 26px -10px var(--cab-bordeaux-glow), inset 0 1px 0 rgba(255,255,255,.25)',
                      border: 'none', cursor: submitDisabled ? 'default' : 'pointer', opacity: submitDisabled ? 0.55 : 1,
                      transition: 'filter .2s, opacity .2s, transform .2s',
                    }}
                    onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.filter = 'brightness(1.04)' }}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'none' }}
                  >
                    {loading ? (
                      <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: '50%', border: '2.5px solid rgba(255,255,255,.5)', borderTopColor: '#fff', display: 'inline-block', animation: 'cabRot .9s linear infinite' }} />
                    ) : (
                      <>{submitLabel}<Ic.arrow style={{ width: 18, height: 18 }} /></>
                    )}
                  </button>

                  {/* pied du formulaire — bascule de mode */}
                  {mode === 'login' && (
                    <div style={{ marginTop: 24, textAlign: 'center', fontSize: 13.5, color: K.ink2 }}>
                      Pas encore de compte ?{' '}
                      <button type="button" onClick={() => { setMode('signup'); setError(''); setConfirmPassword('') }} style={{ fontWeight: 600, color: K.bordeaux, background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.sans, padding: 0 }}>
                        Démarrer l'essai gratuit
                      </button>
                      <div style={{ marginTop: 8, fontSize: 12, color: K.ink3, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <Ic.spark style={{ width: 13, height: 13, color: K.gild }} />
                        Sans engagement · votre cabinet opérationnel en quelques minutes
                      </div>
                    </div>
                  )}
                  {mode === 'signup' && (
                    <div style={{ marginTop: 20, textAlign: 'center' }}>
                      <button type="button" onClick={() => { setMode('login'); setError(''); setConfirmPassword('') }} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: K.ink2, background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.sans }}>
                        <Ic.back style={{ width: 15, height: 15 }} />Déjà un compte ? Se connecter
                      </button>
                    </div>
                  )}
                  {(mode === 'forgot' || mode === 'recovery') && (
                    <div style={{ marginTop: 20, textAlign: 'center' }}>
                      <button type="button" onClick={() => { setMode('login'); setError('') }} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: K.ink2, background: 'none', border: 'none', cursor: 'pointer', fontFamily: F.sans }}>
                        <Ic.back style={{ width: 15, height: 15 }} />Retour à la connexion
                      </button>
                    </div>
                  )}
                </form>
              )}
            </div>

            {/* ── Mentions de confiance (pied du volet droit) ── */}
            <div style={{ marginTop: isMobile ? 26 : 30, paddingBottom: isMobile ? 30 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap', fontSize: 11.5, color: K.ink3 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Ic.shieldLine style={{ width: 13, height: 13, color: K.ink4, flex: 'none' }} />
                Données en UE
              </span>
              <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: K.hair3 }} />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>RGPD</span>
              <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: K.hair3 }} />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Secret professionnel</span>
            </div>
          </div>
        </section>

      </main>
    </div>
  )
}
